import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  AuthRedirectService,
  AuthRedirectStorageService,
  FeatureConfigService,
  GlobalMessageService,
  GlobalMessageType,
  RoutingService,
  SemanticPathService,
  WindowRef
} from '@spartacus/core';
import { LoginFormComponentService } from '@spartacus/user/account/components';
import { UserProfileFacade } from '@spartacus/user/profile/root';
import { from, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, pairwise, takeWhile, tap, withLatestFrom } from 'rxjs/operators';
import { CustomAuthService } from 'src/app/services/auth/custom-auth.service';

@Injectable({
  providedIn: 'root'
})
export class CustomLoginFormComponentService extends LoginFormComponentService implements OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  protected newFeatureConfigService = inject(FeatureConfigService);
  protected activeCartService = inject(ActiveCartFacade);
  isEnableAuthCodeFlowByDefault: boolean = false;

  constructor(
    protected auth: CustomAuthService,
    protected globalMessage: GlobalMessageService,
    protected winRef: WindowRef,
    protected router: Router,
    protected semanticPathService: SemanticPathService,
    protected routingService: RoutingService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    protected authRedirectStorageService: AuthRedirectStorageService,
    protected userProfile: UserProfileFacade,
    protected authRedirectService: AuthRedirectService
  ) {
    super(auth, globalMessage, winRef);
    this.isEnableAuthCodeFlowByDefault = this.newFeatureConfigService.isEnabled('authorizationCodeFlowByDefault');
  }

  override login(nativeForm?: HTMLFormElement): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      const navigationUrl = window.location.pathname;
      const checkoutLoginUrl = this.semanticPathService.get('checkoutLogin');

      if (navigationUrl.includes(checkoutLoginUrl!)) {
        this.authRedirectStorageService.setRedirectUrl(this.semanticPathService.get('cart'));
      }
    }

    this.subscriptions.add(
      this.activeCartService
        .getActive()
        .pipe(
          filter((cart) => Boolean(cart.code)),
          distinctUntilChanged((prev, curr) => prev.code === curr.code),
          pairwise(),
          filter(([prev, curr]) => {
            return curr.customer?.uid !== 'anonymous' && (prev.totalItems || 0) < curr.totalItems;
          })
        )
        .subscribe(() => {
          this.globalMessage.add({ key: 'checkoutLogin.checkYourCart' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
        })
    );

    if (this.isEnableAuthCodeFlowByDefault && nativeForm) {
      this.setOauthRedirectFlowFlag();
      const newNativeForm = this.generateNewNativeForm();
      if (newNativeForm && newNativeForm instanceof HTMLFormElement) {
        newNativeForm?.submit();
      } else {
        nativeForm?.submit();
      }
      this.busy$.next(true);
    } else {
      this.busy$.next(true);

      from(
        this.auth.loginWithCredentials(
          // TODO: consider dropping toLowerCase as this should not be part of the UI,
          // as it's too opinionated and doesn't work with other AUTH services
          this.form.value.userId.toLowerCase(),
          this.form.value.password
        )
      )
        .pipe(
          withLatestFrom(this.auth.isUserLoggedIn()),
          tap(([_, isLoggedIn]) => this.onSuccess(isLoggedIn)),
          withLatestFrom(this.activeCartService.getActive())
        )
        .subscribe();
    }

    // this.subscriptions.add(
    //   from(
    //     this.auth.loginWithCredentialsWithoutRedirect(
    //       // TODO: consider dropping toLowerCase as this should not be part of the UI,
    //       // as it's too opinionated and doesn't work with other AUTH services
    //       this.form.value.userId.toLowerCase(),
    //       this.form.value.password
    //     )
    //   )
    //     .pipe(
    //       withLatestFrom(this.auth.isUserLoggedIn()),
    //       tap(([_, isLoggedIn]) => this.onSuccess(isLoggedIn)),
    //       withLatestFrom(this.activeCartService.getActive())
    //     )
    //     .subscribe()
    // );
  }

  protected override onSuccess(isLoggedIn: boolean): void {
    let userDataExist = false;
    this.document.body.classList.remove('hide-alert-danger');
    if (isLoggedIn) {
      this.subscriptions.add(
        this.userProfile
          .get()
          .pipe(takeWhile(() => !userDataExist))
          .subscribe((data: any) => {
            if (data !== undefined) {
              this.winRef?.localStorage?.setItem('userProfessional', data?.professional);
              this.authRedirectService.redirect();
              userDataExist = true;
            }
          })
      );
      if (isPlatformBrowser(this.platformId)) {
        this.document.body.classList.add('hide-alert-danger');
        setTimeout(() => {
          this.document.body.classList.remove('hide-alert-danger');
          this.globalMessage.remove(GlobalMessageType.MSG_TYPE_ERROR);
        }, 4000);
      }
      this.form.reset();
    }
    this.busy$.next(false);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  private generateNewNativeForm(): HTMLFormElement | null {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return null;
    }
    const userId = this.form.value.userId;
    const password = this.form.value.password;
    const csrfToken = this.form.value.csrf;

    const nativeForm = document.createElement('form');
    nativeForm.method = this.method ?? 'POST';
    nativeForm.action = this.action ?? '';

    // Spring UsernamePasswordAuthenticationFilter  username
    this.addHiddenInput(document, nativeForm, 'username', userId);
    this.addHiddenInput(document, nativeForm, 'password', password);

    this.addHiddenInput(document, nativeForm, '_csrf', csrfToken);

    document.body.appendChild(nativeForm);
    return nativeForm;
  }

  private addHiddenInput(document: Document, form: HTMLFormElement, name: string, value: string): void {
    const input = document.createElement('input');

    input.type = 'hidden';
    input.name = name;
    input.value = value;

    form.appendChild(input);
  }
}
